import { Task } from '../api';
import { InvestmentSplitWithAnswers } from './utils';

const ALLOCATION_TASK_KEY = 'allocationTask';

export type AllocationReports = {
  [programName: string]:
    | {
        [year: number]: number | undefined;
      }
    | undefined;
};

export interface AllocationTask {
  finishedIndexes: number[];
  reports: AllocationReports;
  isReportCreationDone: {
    [investmentIndex: number]: boolean | undefined;
  };
  allocation: Required<InvestmentSplitWithAnswers>[];
  // Allocation state of the investment when this progress was stored. Cached
  // progress is only valid while the server still agrees with it.
  signature: string;
}

type AllocationTaskStorage = {
  [id: string]: AllocationTask | undefined;
};

const part = (value: number | string | undefined | null): string =>
  value === undefined || value === null ? '' : String(value);

export const getAllocationSignature = (task: Task): string => {
  // A task reaches the wizard either through the invitation or, when it is
  // re-opened from the donations page, through the allocation.
  const investment = task.invitation?.investment || task.allocation?.investment;
  return [
    part(investment?.dbId),
    part(investment?.donation),
    part(investment?.totalAllocated),
    part(investment?.unallocatedAmount),
    part(investment?.allocationState),
  ].join('|');
};

export const getTaskInvestmentId = (task: Task): number | undefined =>
  task.invitation?.investment?.dbId || task.allocation?.investment?.dbId;

const getTaskStorageKey = (task: Task): string => {
  const investmentId = getTaskInvestmentId(task);
  return investmentId ? `${investmentId}:${task.taskId}` : String(task.taskId);
};

const getAllocation = (): AllocationTaskStorage => {
  const investmentFinalSplit = window.localStorage.getItem(ALLOCATION_TASK_KEY);
  if (!investmentFinalSplit) return {};
  try {
    const allocationTask: AllocationTaskStorage = JSON.parse(
      investmentFinalSplit,
    );
    return allocationTask;
  } catch (e) {
    return {};
  }
};

export const deleteAllocationTask = (task: Task) => {
  const allocationTask = getAllocation();
  allocationTask[getTaskStorageKey(task)] = undefined;
  allocationTask[String(task.taskId)] = undefined;
  window.localStorage.setItem(
    ALLOCATION_TASK_KEY,
    JSON.stringify(allocationTask),
  );
};

export const getAllocationByTask = (task: Task): AllocationTask | undefined => {
  const allocationTask = getAllocation();
  const storageKey = getTaskStorageKey(task);
  const legacyKey = String(task.taskId);
  const stored = allocationTask[storageKey] || allocationTask[legacyKey];
  if (!stored) return undefined;
  if (storageKey !== legacyKey && allocationTask[legacyKey]) {
    allocationTask[storageKey] = allocationTask[legacyKey];
    allocationTask[legacyKey] = undefined;
    window.localStorage.setItem(
      ALLOCATION_TASK_KEY,
      JSON.stringify(allocationTask),
    );
  }
  // Detaching an allocation, or any allocation written elsewhere, happens
  // server-side and cannot clear this entry. Anything stored against a state
  // the server has moved on from would replay a finished allocation that no
  // longer exists, so it is dropped instead. Entries from before signatures
  // existed have none and are dropped the same way.
  if (stored.signature !== getAllocationSignature(task)) {
    deleteAllocationTask(task);
    return undefined;
  }
  return stored;
};

export const setAllocationTask = (
  task: Task,
  finishedIndexes: Set<number>,
  reports: AllocationReports,
  investmentFinalSplit: Required<InvestmentSplitWithAnswers>[],
  isReportCreationDone: AllocationTask['isReportCreationDone'],
) => {
  const allocationTask = getAllocation();
  allocationTask[getTaskStorageKey(task)] = {
    reports,
    allocation: investmentFinalSplit,
    finishedIndexes: [...finishedIndexes],
    isReportCreationDone,
    signature: getAllocationSignature(task),
  };
  window.localStorage.setItem(
    ALLOCATION_TASK_KEY,
    JSON.stringify(allocationTask),
  );
};
